/**
 * Page-level JSON-LD for the free tool pages (/tools/:slug). Emits a Service (the free
 * tool itself), a BreadcrumbList, and a FAQPage built from the same questions the page
 * renders, so the ~28 tool pages become rich-result and citation eligible. AnyAPI is
 * always the provider, referenced by @id from orgSchema. Mirrors apiSchema.ts.
 *
 * `Service` rather than `SoftwareApplication` for the reason `appSchema.ts` states: the
 * Software App rich result requires `aggregateRating` or `review`, these tools have
 * neither, and the type reported a Search Console error on every tool page instead.
 *
 * The FAQ text has one owner (`data/toolSeo.ts`) - schema must match what a crawler sees.
 */
import type { ToolFaq } from '../../data/toolSeo';
import type { ToolMetadata } from '../../data/toolCatalog';
import { breadcrumbList, TOOLS_STEP } from './breadcrumb';
import { ORG_ID, SITE_ORIGIN } from './orgSchema';

const TOOLS_URL = `${SITE_ORIGIN}/tools`;

/** Canonical URL for one tool; the empty slug is the tools index. */
export const toolUrl = (slug: string): string => (slug ? `${TOOLS_URL}/${slug}` : TOOLS_URL);

const question = (faq: ToolFaq) => ({
  '@type': 'Question',
  name: faq.q,
  acceptedAnswer: { '@type': 'Answer', text: faq.a },
});

// The tools index is its own last step, so it passes no leaf.
const breadcrumb = (url: string, name: string) =>
  breadcrumbList(TOOLS_STEP, ...(url === TOOLS_URL ? [] : [{ name, url }]));

/**
 * The page's ordered steps as a HowTo.
 *
 * Google renders HowTo for a page that teaches a procedure, and every tool page
 * teaches one. The steps are the same list the page renders from
 * `data/toolGuide.ts`, so the node never describes a procedure a reader cannot see.
 */
const howTo = (url: string, name: string, steps: readonly string[]) => ({
  '@type': 'HowTo',
  '@id': `${url}#howto`,
  name: `How to use ${name}`,
  step: steps.map((text, index) => ({
    '@type': 'HowToStep',
    position: index + 1,
    text,
    url: `${url}#step${index + 1}`,
  })),
});

/** Service + BreadcrumbList + HowTo + FAQPage graph for one free tool page. */
export function toolSchemaGraph(
  slug: string,
  metadata: ToolMetadata,
  faq: readonly ToolFaq[],
  steps: readonly string[] = [],
) {
  const url = toolUrl(slug);
  const service = {
    '@type': 'Service',
    '@id': `${url}#service`,
    name: metadata.title,
    description: metadata.description,
    url,
    serviceType: 'Free online data tool',
    areaServed: 'Worldwide',
    provider: { '@id': ORG_ID },
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
  };
  const faqPage = {
    '@type': 'FAQPage',
    '@id': `${url}#faq`,
    mainEntity: faq.map(question),
  };
  const graph = [
    ...(slug ? [service] : []),
    breadcrumb(url, metadata.title),
    ...(steps.length ? [howTo(url, metadata.title, steps)] : []),
    ...(faq.length ? [faqPage] : []),
  ];
  return { '@context': 'https://schema.org', '@graph': graph };
}
